import { useEffect, useRef, useState } from 'react'
import { useNetworkMonitor } from '../../providers/NetworkMonitorProvider'
import type {
  UsePlayerNetworkRecoveryParams,
  UsePlayerNetworkRecoveryReturn,
} from './types'

/**
 * Handles automatic recovery from network errors during video playback.
 *
 * Scenarios:
 * 1. Regular playback: Player's recoverFromNetworkError() handles stream reload
 * 2. After ads: Explicitly loads content if video has no source (readyState === 0)
 *
 * Suppresses error modals during network issues and auto-recovers when reconnected.
 *
 * @param params.playerRef - Player instance with recoverFromNetworkError method
 * @param params.videoElementRef - Video HTML element for readyState checks
 * @param params.videoUrl - Video URL to load if no source (ads scenario)
 * @returns setHadNetworkError - Setter to flag network errors
 */
function usePlayerNetworkRecovery({
  playerRef,
  videoElementRef,
  videoUrl,
}: UsePlayerNetworkRecoveryParams): UsePlayerNetworkRecoveryReturn {
  const { isDisconnected, connectionRecovered, setSuppressAutoErrorModal } =
    useNetworkMonitor()
  const [hadNetworkError, setHadNetworkError] = useState(false)
  const attemptedRecoveryRef = useRef(false)
  const playbackTimeoutRef = useRef<number | null>(null)
  const cooldownTimeoutRef = useRef<number | null>(null)

  // Suppress error modal when network drops
  useEffect(() => {
    if (isDisconnected) {
      setSuppressAutoErrorModal(true)
    }
  }, [isDisconnected, setSuppressAutoErrorModal])

  // Attempt recovery when connection is restored
  useEffect(() => {
    if (
      !connectionRecovered ||
      !hadNetworkError ||
      attemptedRecoveryRef.current
    ) {
      return
    }

    attemptedRecoveryRef.current = true

    // ============================================================================
    // Helper Functions
    // ============================================================================

    const callPlay = () => {
      const playResult = playerRef.current?.play?.()
      if (playResult && typeof playResult.then === 'function') {
        playResult.catch(() => {
          // Expected in some scenarios (autoplay policy, etc)
        })
      }
    }

    const waitForReadyAndPlay = () => {
      const videoElement = videoElementRef?.current
      if (!videoElement) return

      const handleLoadedData = () => {
        videoElement.removeEventListener('loadeddata', handleLoadedData)
        callPlay()
      }
      videoElement.addEventListener('loadeddata', handleLoadedData)
      playbackTimeoutRef.current = window.setTimeout(() => {
        videoElement.removeEventListener('loadeddata', handleLoadedData)
        callPlay()
      }, 5000)
    }

    const attemptPlayback = () => {
      const videoElement = videoElementRef?.current
      if (!videoElement) return

      // Ads scenario: Load content if no source (readyState 0) - only for VOD with videoUrl
      if (
        videoUrl &&
        videoElement.readyState === 0 &&
        playerRef.current?.load
      ) {
        playerRef.current.load(videoUrl)
        waitForReadyAndPlay()
      } else if (videoElement.readyState >= 2) {
        // Video ready: Play immediately
        callPlay()
      } else {
        // Video loading: Wait for ready
        waitForReadyAndPlay()
      }
    }

    const handleSuccessfulRecovery = () => {
      setSuppressAutoErrorModal(false)
      setHadNetworkError(false)
      // Only attempt playback if videoElementRef provided (VOD scenario)
      if (videoElementRef) {
        attemptPlayback()
      }
      cooldownTimeoutRef.current = window.setTimeout(() => {
        attemptedRecoveryRef.current = false
      }, 5000)
    }

    if (playerRef.current?.recoverFromNetworkError) {
      const result = playerRef.current.recoverFromNetworkError()

      if (result instanceof Promise) {
        result.then((success: boolean) => {
          if (success) {
            handleSuccessfulRecovery()
          } else {
            setSuppressAutoErrorModal(false)
            attemptedRecoveryRef.current = false
          }
        })
      } else if (result) {
        handleSuccessfulRecovery()
      } else {
        setSuppressAutoErrorModal(false)
        attemptedRecoveryRef.current = false
      }
    } else {
      setSuppressAutoErrorModal(false)
      attemptedRecoveryRef.current = false
    }
  }, [
    connectionRecovered,
    hadNetworkError,
    playerRef,
    videoElementRef,
    videoUrl,
    setSuppressAutoErrorModal,
  ])

  useEffect(
    () => () => {
      if (playbackTimeoutRef.current !== null) {
        clearTimeout(playbackTimeoutRef.current)
      }
      if (cooldownTimeoutRef.current !== null) {
        clearTimeout(cooldownTimeoutRef.current)
      }
    },
    []
  )

  return {
    setHadNetworkError,
  }
}

export default usePlayerNetworkRecovery
